import { env } from "@/config/env";
import axios from "axios";

// FidexaHub: instância axios própria, separada de `api` (src/api/index.ts).
// A sessão do cliente final é um bearer OPACO (MemberSession.Id, não um
// JWT) — sem exp/refresh token, então nada do interceptor de renovação do
// `api` principal se aplica aqui. Storage key própria, nunca compartilhada
// com `access_token`/`refresh_token` de agente.
const FIDEXA_HUB_SESSION_STORAGE_KEY = "fidexa_hub_session_token";

export const getFidexaHubSessionToken = (): string | null =>
  localStorage.getItem(FIDEXA_HUB_SESSION_STORAGE_KEY);

export const setFidexaHubSessionToken = (token: string): void => {
  localStorage.setItem(FIDEXA_HUB_SESSION_STORAGE_KEY, token);
};

export const clearFidexaHubSessionToken = (): void => {
  localStorage.removeItem(FIDEXA_HUB_SESSION_STORAGE_KEY);
};

// "Quem sou eu" — usado pra recalcular `fromMe` comparando com
// senderMemberId nas mensagens (broadcast-safe, ver
// ResponseFidexaHubMessageJson.FromMe). Sem isso o cliente final não
// distinguia suas próprias mensagens das do amigo num chat
// MemberMember quando elas chegavam via SSE. Refactor Customer→Member:
// o Member é a identidade cross-tenant (sem empresa nenhuma envolvida).
const FIDEXA_HUB_MEMBER_ID_STORAGE_KEY = "fidexa_hub_member_id";

export const getFidexaHubMemberId = (): string | null =>
  localStorage.getItem(FIDEXA_HUB_MEMBER_ID_STORAGE_KEY);

export const setFidexaHubMemberId = (memberId: string): void => {
  localStorage.setItem(FIDEXA_HUB_MEMBER_ID_STORAGE_KEY, memberId);
};

// Convite acessado sem sessão ativa: guarda o token (sessionStorage — só
// dura a aba/sessão do navegador) pra retomar o resgate assim que o
// resume por OTP terminar, sem o customer precisar clicar no link de novo.
const FIDEXA_HUB_PENDING_INVITE_STORAGE_KEY = "fidexa_hub_pending_invite";

// Convite PÚBLICO de uma loja (/hub/loja/:token) — chave separada da de
// convite entre amigos porque os dois retomam em telas diferentes depois
// do OTP, e guardar os dois no mesmo lugar deixaria o resume sem saber pra
// onde voltar. Guardar um limpa o outro: só existe uma retomada pendente.
const FIDEXA_HUB_PENDING_COMPANY_INVITE_STORAGE_KEY =
  "fidexa_hub_pending_company_invite";

// Expired/used magic link (/hub/link/:token) opened without a session: after
// OTP login we go back to the link page, which then opens the logged-in
// member's own chat with the company that issued it.
const FIDEXA_HUB_PENDING_MAGIC_LINK_STORAGE_KEY =
  "fidexa_hub_pending_magic_link";

export const getPendingMagicLinkToken = (): string | null =>
  sessionStorage.getItem(FIDEXA_HUB_PENDING_MAGIC_LINK_STORAGE_KEY);

export const setPendingMagicLinkToken = (token: string): void => {
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_INVITE_STORAGE_KEY);
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_COMPANY_INVITE_STORAGE_KEY);
  sessionStorage.setItem(FIDEXA_HUB_PENDING_MAGIC_LINK_STORAGE_KEY, token);
};

export const clearPendingMagicLinkToken = (): void => {
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_MAGIC_LINK_STORAGE_KEY);
};

export const getPendingInviteToken = (): string | null =>
  sessionStorage.getItem(FIDEXA_HUB_PENDING_INVITE_STORAGE_KEY);

export const setPendingInviteToken = (token: string): void => {
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_COMPANY_INVITE_STORAGE_KEY);
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_MAGIC_LINK_STORAGE_KEY);
  sessionStorage.setItem(FIDEXA_HUB_PENDING_INVITE_STORAGE_KEY, token);
};

export const clearPendingInviteToken = (): void => {
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_INVITE_STORAGE_KEY);
};

export const getPendingCompanyInviteToken = (): string | null =>
  sessionStorage.getItem(FIDEXA_HUB_PENDING_COMPANY_INVITE_STORAGE_KEY);

export const setPendingCompanyInviteToken = (token: string): void => {
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_INVITE_STORAGE_KEY);
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_MAGIC_LINK_STORAGE_KEY);
  sessionStorage.setItem(
    FIDEXA_HUB_PENDING_COMPANY_INVITE_STORAGE_KEY, token);
};

export const clearPendingCompanyInviteToken = (): void => {
  sessionStorage.removeItem(FIDEXA_HUB_PENDING_COMPANY_INVITE_STORAGE_KEY);
};

export const fidexaHubApi = axios.create({
  baseURL: `${env.API_URL}`,
});

fidexaHubApi.interceptors.request.use(config => {
  const token = getFidexaHubSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Motivo do fim da sessão, para a tela de resume conseguir explicar por que
// o cliente voltou pro formulário. Fica em sessionStorage (não em state)
// porque "isso não funcionou" tem como primeira reação um F5 — e um aviso
// que some justamente no reload não serve pra nada. Dura só a aba, some
// sozinho quando ela fecha.
const FIDEXA_HUB_SESSION_END_REASON_STORAGE_KEY =
  "fidexa_hub_session_end_reason";

export const getFidexaHubSessionEndReason = (): string | null =>
  sessionStorage.getItem(FIDEXA_HUB_SESSION_END_REASON_STORAGE_KEY);

export const setFidexaHubSessionEndReason = (reason: string): void => {
  sessionStorage.setItem(FIDEXA_HUB_SESSION_END_REASON_STORAGE_KEY, reason);
};

export const clearFidexaHubSessionEndReason = (): void => {
  sessionStorage.removeItem(FIDEXA_HUB_SESSION_END_REASON_STORAGE_KEY);
};

// Registrado pelo FidexaHubAuthProvider (fora de um componente React, este
// módulo não tem como chamar o `logout()` do contexto diretamente). Sem
// isso, um 401 só limpava o localStorage e o React state de `isAuthenticated`
// ficava desatualizado — a ProtectedRoute nunca redirecionava pro resume, e
// toda request seguinte saía sem Authorization, gerando um loop silencioso
// de 401 (lista de chats aparecia vazia pra sempre, sem erro visível).
let onUnauthorized: (() => void) | null = null;

export const setFidexaHubUnauthorizedHandler = (handler: () => void): void => {
  onUnauthorized = handler;
};

// Dispara o mesmo tratamento de 401 do interceptor a partir de quem NÃO
// passa por ele. Hoje: os canais de SSE, que autenticam com o mesmo bearer
// mas vão por fetchEventSource — sem isso, uma sessão revogada com o
// cliente parado no chat só produzia retry infinito, tela viva e nenhuma
// mensagem chegando.
export const notifyFidexaHubUnauthorized = (): void => {
  clearFidexaHubSessionToken();
  onUnauthorized?.();
};

// Sessão inválida/expirada/revogada (backend responde 401) — não há
// refresh: limpa o storage, avisa o contexto de auth e força o resume por
// OTP. Nunca redireciona pro /auth de agente (namespace de auth totalmente
// separado).
fidexaHubApi.interceptors.response.use(
  response => response,
  error => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      clearFidexaHubSessionToken();
      onUnauthorized?.();
    }
    return Promise.reject(error);
  }
);
